import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import VariantDisplay from '../components/VariantDisplay';
import ProductOptionsDisplay from '../components/CustomOptionsDisplay';
import StarRating from '../components/StarRating';
import { 
  ArrowLeft, Briefcase, DollarSign, Clock, MapPin, Edit, Trash2,
  Calendar, Shield, Package, ChevronLeft, ChevronRight, User,
  CheckCircle, AlertCircle, Phone, Mail
} from 'lucide-react';
import { getImageUrl, getFirstImage } from '../utils/imageHelper';
import ListingImage from '../components/ListingImage';

// ─── Booking / Enquiry Widget ─────────────────────────────────────────────────
function ServiceBookingWidget({ service, data, basePrice }) {
  const isEnquiry = service?.booking_type === 'enquiry';
  const subcategory = (service?.subcategory || service?.subcategory_name || '').toLowerCase();
  const isCaterer   = subcategory.includes('cater');
  const isDecorator = subcategory.includes('decor');
  const isPhoto     = subcategory.includes('photo') || subcategory.includes('video');
  const isHall      = subcategory.includes('hall') || subcategory.includes('venue');
  const today       = new Date().toISOString().split('T')[0];

  const [selectedDate, setSelectedDate] = React.useState('');
  const [guests, setGuests]             = React.useState('');
  const [eventType, setEventType]       = React.useState('');
  const [shootType, setShootType]       = React.useState('');
  const [hours, setHours]               = React.useState('');
  const [message, setMessage]           = React.useState('');

  const priceUnit = data?.chargeType === 'hourly' ? '/hr'
    : data?.chargeType === 'per_session' ? '/session'
    : data?.chargeType === 'daily' ? '/day'
    : data?.priceType === 'hourly' ? '/hr'
    : '';

  return (
    <div className="rounded-xl border-2 border-indigo-100 bg-gradient-to-b from-indigo-50/40 to-white p-4 space-y-4" data-testid="service-booking-widget">
      <div className="flex items-center gap-2">
        {isEnquiry ? <Mail className="h-4 w-4 text-indigo-600" /> : <Calendar className="h-4 w-4 text-indigo-600" />}
        <h3 className="font-semibold text-gray-800">
          {isEnquiry ? 'Request a Quote' : 'Book This Service'}
        </h3>
        <span className="ml-auto text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">Preview</span>
      </div>

      {/* Date Picker */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Preferred Date</label>
        <input type="date" min={today} value={selectedDate} onChange={e => setSelectedDate(e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 bg-white"
          data-testid="service-date-picker" />
      </div>

      {/* Caterer / Decorator / Hall specific inputs */}
      {(isCaterer || isDecorator || isHall) && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{isHall ? 'Expected Guests' : 'No. of Guests'}</label>
            <input type="number" min="1" placeholder="e.g. 150" value={guests} onChange={e => setGuests(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
              data-testid="service-guests-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Event Type</label>
            <select value={eventType} onChange={e => setEventType(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 bg-white"
              data-testid="service-event-type">
              <option value="">Select type…</option>
              <option>Wedding / Sangeet</option>
              <option>Corporate Event</option>
              <option>Birthday Party</option>
              <option>Engagement</option>
              <option>Conference</option>
              <option>Other</option>
            </select>
          </div>
        </div>
      )}

      {/* Photo / Videography inputs */}
      {isPhoto && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Shoot Type</label>
            <select value={shootType} onChange={e => setShootType(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 bg-white"
              data-testid="service-shoot-type">
              <option value="">Select…</option>
              <option>Wedding</option><option>Pre-Wedding</option>
              <option>Product</option><option>Corporate / Event</option>
              <option>Portrait</option><option>Maternity</option><option>Other</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Duration (hrs)</label>
            <input type="number" min="1" max="24" placeholder="e.g. 6" value={hours} onChange={e => setHours(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
              data-testid="service-hours-input" />
          </div>
        </div>
      )}

      {/* Message / requirements */}
      {(isEnquiry || (!isCaterer && !isDecorator && !isHall && !isPhoto)) && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            {isEnquiry ? 'Your Requirements' : 'Additional Notes'}
          </label>
          <textarea rows={3} placeholder={isEnquiry ? 'Describe what you need…' : 'Any special requests?'}
            value={message} onChange={e => setMessage(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 resize-none"
            data-testid="service-message-input" />
        </div>
      )}

      {/* Price summary */}
      {basePrice > 0 && (
        <div className="flex items-center justify-between text-sm bg-white border border-gray-100 rounded-lg px-3 py-2">
          <span className="text-gray-500">Starting from</span>
          <span className="font-bold text-gray-900 text-base">
            ₹{basePrice.toLocaleString('en-IN')}<span className="text-xs font-normal text-gray-400">{priceUnit}</span>
          </span>
        </div>
      )}

      <button type="button" disabled
        className="w-full h-10 rounded-lg bg-indigo-700 text-white font-semibold text-sm flex items-center justify-center gap-2 opacity-80 cursor-not-allowed"
        data-testid="service-book-btn">
        {isEnquiry ? <><Mail className="h-4 w-4" />&nbsp;Send Enquiry</> : <><Calendar className="h-4 w-4" />&nbsp;Check Availability</>}
      </button>
      <p className="text-xs text-center text-gray-400">Preview only — bookings go through Shopify storefront</p>
    </div>
  );
}

const ServiceDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [loading, setLoading] = React.useState(true);
  const [deleting, setDeleting] = React.useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [variantSelections, setVariantSelections] = useState({});
  const [variantPriceAdjustment, setVariantPriceAdjustment] = useState(0);
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  
  React.useEffect(() => {
    const fetchService = async () => {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const token = sessionStorage.getItem('token');
        
        const response = await fetch(`${BACKEND_URL}/api/listings/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setService(data);
        } else {
          console.error('Failed to fetch service');
          navigate('/listings/services');
        }
      } catch (error) {
        console.error('Error fetching service:', error);
        navigate('/listings/services');
      } finally {
        setLoading(false);
      }
    };
    
    if (id) {
      fetchService();
    }
  }, [id, navigate]);
  
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this service? This action cannot be undone.')) {
      return;
    }
    
    setDeleting(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = sessionStorage.getItem('token');
      
      const response = await fetch(`${BACKEND_URL}/api/listings/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        alert('Service deleted successfully');
        navigate('/listings/services');
      } else {
        alert('Failed to delete service');
      }
    } catch (error) {
      console.error('Error deleting service:', error);
      alert('Error deleting service');
    } finally {
      setDeleting(false);
    }
  };

  const handleVariantSelectionChange = ({ selections, priceAdjustment }) => {
    setVariantSelections(selections);
    setVariantPriceAdjustment(priceAdjustment || 0);
  };

  const nextImage = () => {
    if (service?.media?.length > 0) {
      setSelectedImage((prev) => (prev + 1) % service.media.length);
    }
  };

  const prevImage = () => {
    if (service?.media?.length > 0) {
      setSelectedImage((prev) => (prev - 1 + service.media.length) % service.media.length);
    }
  };

  const getDisplayPrice = () => {
    const basePrice = parseFloat(service?.data?.price || service?.data?.hourlyRate || service?.data?.dailyRate || 0);
    // If custom options have calculated a price, use that as base
    if (calculatedPrice !== null) {
      return calculatedPrice + variantPriceAdjustment;
    }
    return basePrice + variantPriceAdjustment;
  };

  const handlePriceChange = (newPrice) => {
    setCalculatedPrice(newPrice);
  };

  const getPriceUnit = () => {
    const priceType = service?.data?.priceType;
    if (priceType === 'hourly') return '/hour';
    if (priceType === 'daily') return '/day';
    return '';
  };
  
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-600">Loading service details...</p>
        </div>
      </DashboardLayout>
    );
  }
  
  if (!service) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-600">Service not found</p>
        </div>
      </DashboardLayout>
    );
  }

  const data = service.data || {};
  const images = service.media || [];
  const basePrice = parseFloat(data.price || data.hourlyRate || data.dailyRate || 0);
  
  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header with Back and Actions */}
        <div className="flex items-center justify-between mb-6 px-4">
          <Button variant="ghost" onClick={() => navigate('/listings/services')} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Services
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(`/listings/services/edit/${id}`)}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              <Trash2 className="h-4 w-4 mr-2" />
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </div>

        {/* Two Column Layout - Matching Product Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 px-4">
          
          {/* LEFT COLUMN - Service Gallery */}
          <div className="space-y-4">
            {/* Main Image with Navigation */}
            <div className="relative bg-white rounded-xl overflow-hidden border aspect-square">
              {images.length > 0 ? (
                <>
                  <ListingImage
                    src={images[selectedImage]}
                    alt={service.title}
                    className="w-full h-full object-contain"
                    showPlaceholder={false}
                  />
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg transition-all"
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg transition-all"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-100">
                  <Briefcase className="h-24 w-24 text-gray-300" />
                </div>
              )}
            </div>

            {/* Thumbnail Gallery */}
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {images.map((url, index) => (
                  <button
                    key={url.id || url.name || `${url.title || "url"}-${index}`}
                    onClick={() => setSelectedImage(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImage === index 
                        ? 'border-blue-600 ring-2 ring-blue-200' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <ListingImage
                      src={url}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1">
                Share
              </Button>
              <Button variant="outline" className="flex-1">
                Save
              </Button>
            </div>

            {/* Tabs for Description, Availability, Policies */}
            <Tabs defaultValue="description" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="description">Description</TabsTrigger>
                <TabsTrigger value="availability">Availability</TabsTrigger>
                <TabsTrigger value="policies">Policies</TabsTrigger>
              </TabsList>
              
              <TabsContent value="description" className="mt-4">
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                      {service.description}
                    </p>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="availability" className="mt-4">
                <Card>
                  <CardContent className="pt-4 space-y-4">
                    {data.locationType && (
                      <div className="flex items-start gap-3">
                        <MapPin className="h-5 w-5 text-gray-500 mt-0.5" />
                        <div>
                          <h4 className="font-medium">Service Location</h4>
                          <p className="text-sm text-gray-600 capitalize">{data.locationType}</p>
                          {data.serviceAddress && <p className="text-sm text-gray-600">{data.serviceAddress}</p>}
                        </div>
                      </div>
                    )}
                    {data.duration && (
                      <div className="flex items-start gap-3">
                        <Clock className="h-5 w-5 text-gray-500 mt-0.5" />
                        <div>
                          <h4 className="font-medium">Duration</h4>
                          <p className="text-sm text-gray-600">{data.duration}</p>
                        </div>
                      </div>
                    )}
                    {data.availability && (
                      <div className="flex items-start gap-3">
                        <Calendar className="h-5 w-5 text-gray-500 mt-0.5" />
                        <div>
                          <h4 className="font-medium">Availability</h4>
                          {/*
                            iter-242 — `data.availability` may be either a
                            legacy STRING ("Mon–Fri, 9am–6pm") or a structured
                            availability object emitted by AvailabilityScheduler
                            with shape {business_hours, slot_duration,
                            bookings_per_slot}. Rendering the object directly
                            caused React #31 ("Objects are not valid as a React
                            child"). We branch on the value's type:
                              • string  → render as-is (legacy data)
                              • object  → summarise enabled days + slot info
                          */}
                          {typeof data.availability === 'string' ? (
                            <p className="text-sm text-gray-600 capitalize">{data.availability}</p>
                          ) : (
                            (() => {
                              const av = data.availability || {};
                              const hours = Array.isArray(av.business_hours) ? av.business_hours : [];
                              const enabledDays = hours
                                .filter((h) => h && h.enabled && h.day)
                                .map((h) => String(h.day).slice(0, 3));
                              return (
                                <div className="text-sm text-gray-600 space-y-1">
                                  {enabledDays.length > 0 && (
                                    <p>{enabledDays.join(', ')}</p>
                                  )}
                                  {av.slot_duration ? (
                                    <p className="text-xs text-gray-500">
                                      {av.slot_duration}-min slots
                                      {av.bookings_per_slot && av.bookings_per_slot > 1
                                        ? ` · up to ${av.bookings_per_slot} bookings per slot`
                                        : ''}
                                    </p>
                                  ) : null}
                                  {enabledDays.length === 0 && !av.slot_duration && (
                                    <p className="text-xs text-gray-500">Schedule on request</p>
                                  )}
                                </div>
                              );
                            })()
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="policies" className="mt-4">
                <Card>
                  <CardContent className="pt-4 space-y-4">
                    {service.service_guarantee?.enabled && (
                      <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Shield className="h-5 w-5 text-green-600" />
                          <h4 className="font-medium text-green-800">Service Guarantee</h4>
                        </div>
                        <p className="text-sm text-green-700">
                          {service.service_guarantee.type === 'full' 
                            ? 'Full Refund Guarantee' 
                            : `${service.service_guarantee.refund_percentage}% Refund`}
                          {service.service_guarantee.days_limit && ` within ${service.service_guarantee.days_limit} days`}
                        </p>
                      </div>
                    )}
                    {data.cancellationPolicy && (
                      <div>
                        <h4 className="font-medium mb-2">Cancellation Policy</h4>
                        <p className="text-sm text-gray-600 whitespace-pre-wrap">{data.cancellationPolicy}</p>
                      </div>
                    )}
                    {data.terms && (
                      <div>
                        <h4 className="font-medium mb-2">Terms & Conditions</h4>
                        <p className="text-sm text-gray-600 whitespace-pre-wrap">{data.terms}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* RIGHT COLUMN - Service Info & Configuration */}
          <div className="space-y-6">
            {/* Title & Status */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge variant={service.status === 'active' ? 'default' : 'secondary'}>
                  {service.status === 'pending_approval' ? 'Pending' : service.status}
                </Badge>
                <Badge variant="outline">{service.booking_type === 'enquiry' ? 'Enquiry Based' : 'Booking Based'}</Badge>
              </div>
              <h1 className="text-2xl font-bold text-gray-900">{service.title}</h1>
              
              {/* Star Rating */}
              <div className="mt-2">
                <StarRating 
                  listingId={id}
                  size="md"
                  showCount={true}
                  fetchData={true}
                />
              </div>
            </div>

            {/* Vendor Info */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                <User className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Service Provider</p>
                <p className="font-medium">{service.vendor_name || 'Provider'}</p>
              </div>
            </div>

            {/* Category */}
            <div className="flex gap-2">
              <Badge variant="outline">{service.category}</Badge>
              {service.subcategory && <Badge variant="outline">{service.subcategory}</Badge>}
            </div>

            {/* Pricing Section */}
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-baseline gap-3">
                <span className="text-3xl font-bold text-gray-900">
                  ₹{getDisplayPrice().toLocaleString()}
                </span>
                <span className="text-lg text-gray-500">{getPriceUnit()}</span>
              </div>
              {variantPriceAdjustment > 0 && (
                <p className="text-sm text-green-600 mt-1">
                  +₹{variantPriceAdjustment.toLocaleString()} for selected options
                </p>
              )}
              {data.chargeTax && (
                <p className="text-xs text-gray-500 mt-1">+ {data.taxRate}% GST applicable</p>
              )}
            </div>

            {/* Service Variants Display */}
            {(service.variants?.length > 0 || service.data?.variants?.length > 0) && (
              <VariantDisplay 
                variants={service.variants?.length > 0 ? service.variants : (service.data?.variants || [])}
                variantCombinations={service.variant_combinations?.length > 0 ? service.variant_combinations : (service.data?.variant_combinations || service.data?.variantCombinations || [])}
                title="Service Options" 
                compact={true}
                basePrice={basePrice}
                onSelectionChange={handleVariantSelectionChange}
              />
            )}

            {/* Custom Options Display - Always render if listing has options data */}
            <ProductOptionsDisplay 
              listingId={id}
              basePrice={basePrice}
              variantSelections={variantSelections}
              variantPriceAdjustment={variantPriceAdjustment}
              onPriceChange={handlePriceChange}
            />

            {/* Service Details */}
            <Card>
              <CardContent className="pt-4 space-y-3">
                {data.duration && (
                  <div className="flex items-center gap-3">
                    <Clock className="h-5 w-5 text-gray-400" />
                    <div>
                      <span className="text-sm text-gray-500">Duration: </span>
                      <span className="font-medium">{data.duration}</span>
                    </div>
                  </div>
                )}
                {data.locationType && (
                  <div className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-gray-400" />
                    <div>
                      <span className="text-sm text-gray-500">Location: </span>
                      <span className="font-medium capitalize">{data.locationType}</span>
                    </div>
                  </div>
                )}
                {service.booking_type && (
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-gray-400" />
                    <div>
                      <span className="text-sm text-gray-500">Booking Type: </span>
                      <span className="font-medium capitalize">{service.booking_type}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* ── Booking / Enquiry Widget ── */}
            <ServiceBookingWidget service={service} data={data} basePrice={getDisplayPrice()} />

            {/* CTA Buttons */}
            <div className="space-y-3">
              {service.booking_type === 'enquiry' ? (
                <Button className="w-full h-12 text-lg bg-gray-900 hover:bg-gray-800" disabled>
                  <Mail className="h-5 w-5 mr-2" />
                  SEND ENQUIRY
                </Button>
              ) : (
                <Button className="w-full h-12 text-lg bg-gray-900 hover:bg-gray-800" disabled>
                  <Calendar className="h-5 w-5 mr-2" />
                  BOOK NOW
                </Button>
              )}
              <Button variant="outline" className="w-full h-12 text-lg" disabled>
                <Phone className="h-5 w-5 mr-2" />
                CONTACT PROVIDER
              </Button>
              <p className="text-xs text-center text-gray-500">
                (Preview mode - Buttons disabled in admin view)
              </p>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-3 gap-4 pt-4 border-t">
              <div className="text-center">
                <CheckCircle className="h-6 w-6 mx-auto text-gray-600 mb-1" />
                <p className="text-xs text-gray-600">Verified</p>
              </div>
              <div className="text-center">
                <Shield className="h-6 w-6 mx-auto text-gray-600 mb-1" />
                <p className="text-xs text-gray-600">Secure</p>
              </div>
              <div className="text-center">
                <AlertCircle className="h-6 w-6 mx-auto text-gray-600 mb-1" />
                <p className="text-xs text-gray-600">Support</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ServiceDetails;